import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFreePlay } from '../../context/FreePlayContext';
import { useAuth } from '../../context/AuthContext';
import { updateGamerTag } from '../../api/backend';
import DashNav from './DashNav';
import './GameDashboard.css';

// Confirmations the backend requires before a payment counts as verified
const VERIFY_CONFIRMATIONS = { BTC: 1, ETH: 12, DOGE: 6, ZEC: 6 };

// On-chain verification badge for a purchase row. Older history predates the
// verification feature and stays 'unchecked' forever, so only recent
// unchecked purchases show the "checking" state.
const verifyBadge = (p) => {
  if (p.verifyStatus === 'verified') {
    return { cls: 'ok', text: '✓ Verified on-chain' };
  }
  if (p.verifyStatus === 'seen') {
    const need = VERIFY_CONFIRMATIONS[p.cryptoCurrency] || 1;
    return { cls: 'wait', text: `⏳ Confirming (${Math.min(p.confirmations || 0, need)}/${need})` };
  }
  if (p.verifyStatus === 'not_found') {
    return { cls: 'miss', text: '❔ Not yet found on-chain' };
  }
  const ageMs = Date.now() - new Date(p.createdAt).getTime();
  if (p.verifyStatus === 'unchecked' && ageMs < 72 * 60 * 60 * 1000) {
    return { cls: 'wait', text: '⏳ Checking blockchain…' };
  }
  return null;
};

const formatDate = (value) => {
  if (!value) {
    return '';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const GameDashboard = () => {
  const { openPayment } = useFreePlay();
  const {
    isAuthenticated,
    loading,
    account,
    spinsRemaining,
    points,
    purchases,
    refreshAccount,
    openAuth
  } = useAuth();
  const navigate = useNavigate();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  // Gamertag editor (same shared gamertag as Bit Tanks — one tag, all games)
  const [tagInput, setTagInput] = useState('');
  const [tagStatus, setTagStatus] = useState(null); // { ok, message }
  const [tagSaving, setTagSaving] = useState(false);

  useEffect(() => {
    setTagInput(account?.gamerTag || '');
  }, [account?.gamerTag]);

  const handleSaveTag = async () => {
    const tag = tagInput.trim();
    setTagStatus(null);
    if (tag.length < 1 || tag.length > 20) {
      setTagStatus({ ok: false, message: 'Gamertag must be 1-20 characters.' });
      return;
    }
    if (!/^[A-Za-z0-9 _]+$/.test(tag)) {
      setTagStatus({ ok: false, message: 'Letters, numbers, spaces, and underscores only.' });
      return;
    }
    setTagSaving(true);
    try {
      await updateGamerTag(account.email, tag);
      await refreshAccount();
      setTagStatus({ ok: true, message: 'Gamertag saved! It shows on the leaderboard.' });
    } catch (err) {
      setTagStatus({ ok: false, message: err.message || 'Could not save the gamertag.' });
    } finally {
      setTagSaving(false);
    }
  };

  // Pull the freshest balance + purchase history from Mongo when the
  // dashboard mounts for a logged-in user.
  useEffect(() => {
    if (isAuthenticated) {
      refreshAccount();
    }
  }, [isAuthenticated, refreshAccount]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = currentDateTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const formattedTime = currentDateTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleBuySpins = () => {
    openPayment('dashboard');
  };

  // Logged-out state: prompt to sign in (data lives in the user's account).
  if (!isAuthenticated) {
    return (
      <div className="game-dashboard">
        <div className="game-dashboard__container">
          <div className="game-dashboard__header">
            <h1 className="game-dashboard__title">Account Dash</h1>
            <button
              onClick={() => navigate('/game')}
              className="game-dashboard__close"
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
          <div className="game-dashboard__stat-card">
            <div className="game-dashboard__stat-label">Not Logged In</div>
            <div className="game-dashboard__spins-content">
              <div className="game-dashboard__spins-info">
                {loading
                  ? 'Loading your account…'
                  : 'Log in or create an account to see your spins balance and purchase history.'}
              </div>
              {!loading && (
                <button className="game-dashboard__buy-button" onClick={() => openAuth('dashboard')}>
                  Log In / Sign Up
                </button>
              )}
            </div>
          </div>
          <DashNav />
          <button className="game-dashboard__bottom-close" onClick={() => navigate('/game')}>
            Close
          </button>
          <a
            className="game-dashboard__home-link"
            href="https://www.satoshiadvance.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit SatoshiAdvance.com
          </a>
          <div className="game-dashboard__datetime">
            {formattedDate} • {formattedTime}
          </div>
        </div>
      </div>
    );
  }

  const totalSpins = account?.totalSpins ?? 0;

  return (
    <div className="game-dashboard">
      <div className="game-dashboard__container">
        <div className="game-dashboard__header">
          <h1 className="game-dashboard__title">Account Dash</h1>
          <button
            onClick={() => navigate('/game')}
            className="game-dashboard__close"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Balance + Stats Card (real data from Mongo) */}
        <div className="game-dashboard__stat-card">
          <div className="game-dashboard__stat-label">Spin Stats</div>
          <div className="game-dashboard__stats-content">
            <div className="game-dashboard__stat-row">
              <div className="game-dashboard__stat-value">{Math.floor(spinsRemaining / 10).toLocaleString()}</div>
              <div className="game-dashboard__stat-text">Games Remaining</div>
            </div>
            <div className="game-dashboard__stat-row">
              <div className="game-dashboard__stat-value">{(account?.gamesPlayed ?? 0).toLocaleString()}</div>
              <div className="game-dashboard__stat-text">Games Played</div>
            </div>
            <div className="game-dashboard__stat-row">
              <div className="game-dashboard__stat-value">{totalSpins.toLocaleString()}</div>
              <div className="game-dashboard__stat-text">Total Spins</div>
            </div>
          </div>
        </div>

        {/* Points / High Score Card (game engine) */}
        <div className="game-dashboard__stat-card">
          <div className="game-dashboard__stat-label">Points</div>
          <div className="game-dashboard__stats-content">
            <div className="game-dashboard__stat-row">
              <div className="game-dashboard__stat-value">{(account?.highScore ?? 0).toLocaleString()}</div>
              <div className="game-dashboard__stat-text">High Score</div>
            </div>
            <div className="game-dashboard__stat-row">
              <div className="game-dashboard__stat-value">{points.toLocaleString()}</div>
              <div className="game-dashboard__stat-text">Lifetime Points</div>
            </div>
            <div className="game-dashboard__stat-row">
              <div className="game-dashboard__stat-value">{(account?.bestSingleSpinPayout ?? 0).toLocaleString()}</div>
              <div className="game-dashboard__stat-text">Best Single Spin</div>
            </div>
          </div>
        </div>

        {/* Player Info Card */}
        <div className="game-dashboard__stat-card">
          <div className="game-dashboard__stat-label">Player Info</div>
          <div className="game-dashboard__player-info">
            {account?.gamerTag && (
              <div className="game-dashboard__player-tag">🎮 {account.gamerTag}</div>
            )}
            <div className="game-dashboard__player-email">{account?.email}</div>
            {account?.signupDate && (
              <div className="game-dashboard__player-since">
                Member since {formatDate(account.signupDate)}
              </div>
            )}
            <div className="game-dashboard__player-status">Status: Active</div>
          </div>
        </div>

        {/* Gamertag Card (shared across all Satoshi Advance games) */}
        <div className="game-dashboard__stat-card">
          <div className="game-dashboard__stat-label">Gamertag</div>
          <div className="game-dashboard__tag-content">
            <div className="game-dashboard__spins-info">
              {account?.gamerTag
                ? <>Playing as <b>{account.gamerTag}</b> — one tag across all Satoshi Advance games.</>
                : 'Create a gamertag to appear on the leaderboard (works across all Satoshi Advance games).'}
            </div>
            <div className="game-dashboard__tag-row">
              <input
                className="game-dashboard__tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="YourGamertag"
                maxLength={20}
              />
              <button
                className="game-dashboard__buy-button game-dashboard__tag-save"
                onClick={handleSaveTag}
                disabled={tagSaving}
              >
                {tagSaving ? 'Saving…' : account?.gamerTag ? 'Update Tag' : 'Create Tag'}
              </button>
            </div>
            {tagStatus && (
              <div
                className={
                  tagStatus.ok
                    ? 'game-dashboard__tag-status game-dashboard__tag-status--ok'
                    : 'game-dashboard__tag-status game-dashboard__tag-status--err'
                }
              >
                {tagStatus.message}
              </div>
            )}
          </div>
        </div>

        {/* Spins / Buy Card */}
        <div className="game-dashboard__stat-card">
          <div className="game-dashboard__stat-label">Your Games</div>
          <div className="game-dashboard__spins-content">
            <div className="game-dashboard__spins-info">
              You have {Math.floor(spinsRemaining / 10).toLocaleString()} games remaining (each game is 10 spins). Purchase more to keep playing!
            </div>
            <button className="game-dashboard__buy-button" onClick={handleBuySpins}>
              Purchase More Spins
            </button>
          </div>
        </div>

        {/* Purchase History Card (real data from Mongo) */}
        <div className="game-dashboard__stat-card">
          <div className="game-dashboard__stat-label">Purchase History</div>
          <div className="game-dashboard__history-content">
            {purchases && purchases.length > 0 ? (
              purchases.map((p, index) => {
                const badge = verifyBadge(p);
                return (
                  <div className="game-dashboard__history-row" key={`${p.createdAt || 'p'}-${index}`}>
                    <div className="game-dashboard__history-main">
                      <span className="game-dashboard__history-spins">+{(p.spins || 0).toLocaleString()} spins</span>
                      <span className="game-dashboard__history-amount">
                        ${Number(p.usdAmount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="game-dashboard__history-meta">
                      {formatDate(p.createdAt)}
                      {p.cryptoCurrency ? ` • ${p.cryptoAmount || ''} ${p.cryptoCurrency}` : ''}
                    </div>
                    {badge && (
                      <div className={`game-dashboard__history-verify game-dashboard__history-verify--${badge.cls}`}>
                        {badge.text}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="game-dashboard__spins-info">No purchases yet.</div>
            )}
          </div>
        </div>

        <DashNav />

        <button className="game-dashboard__bottom-close" onClick={() => navigate('/game')}>
          Close
        </button>

        <a
          className="game-dashboard__home-link"
          href="https://www.satoshiadvance.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Visit SatoshiAdvance.com
        </a>

        {/* Date/Time Display */}
        <div className="game-dashboard__datetime">
          {formattedDate} • {formattedTime}
        </div>
      </div>
    </div>
  );
};

export default GameDashboard;
